import React from 'react';
import Box from '@ntuctech/devex-tangram/Box';
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained';
import ButtonIcon from '@ntuctech/devex-tangram/Button/ButtonIcon';
import SvgLocation from '@ntuctech/devex-tangram/Icon/SvgLocation';
import Input from '@ntuctech/devex-tangram/Input';
import Typography from '@ntuctech/devex-tangram/Typography';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import {
  handleAddressInputChange,
  renderSearchResult,
  searchAddress,
} from '../../services/address-search-utils';
import FormAction from '../FormAction';
import FormWrapper from '../FormWrapper';
import FullAddressForm from '../FullAddressForm';
import ProfileAction from '../ProfileAction';
import ProfileContent from '../ProfileContent';

const STYLED = {};

STYLED.SearchPostalCode = styled.div`
  width: 100%;
  position: relative;
`;

STYLED.SearchResultDropdown = styled.div`
  position: relative;
  top: -1.25rem;

  @media (max-width: 767px) {
    top: -0.75rem;
  }
`;

STYLED.ResultListWrapper = styled.div`
  border: 1px solid #eaeaea;
  box-sizing: border-box;
  border-radius: 0.25rem;
  width: 100%;
  max-height: 12rem;
  padding: 0 1rem;
  position: absolute;
  top: 0;
  z-index: 1;
  background: white;
  overflow: auto;
  margin-bottom: 2rem;

  & .recent-search {
    margin-top: 2.25rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  & .suggestion-item {
    cursor: pointer;
    padding: 1rem 0;
    border-bottom: 1px solid #d8d8d8;
    position: relative;
  }

  & .suggestion-item-bg {
    width: calc(100% + 2rem);
    height: 100%;
    position: absolute;
    z-index: 2;
    left: -1rem;
    top: 0;
    will-change: background-color;
    transition: background-color ease 0.3s;
  }

  & .right-arrow-icon {
    display: none;
    width: 1.5rem;
    height: 1.5rem;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  & .main-text {
    color: #000000;
    position: relative;
    z-index: 3;
  }

  & .secondary-text {
    color: #666666;
    position: relative;
    z-index: 3;
  }

  & > div:last-child {
    border-bottom: none;
  }
`;

const ContactDetailsSc = styled.div`
  margin: 1.5rem auto;
`;

export const DisplayCustDetails = ({ name }) => {
  return (
    <ContactDetailsSc>
      <Typography variant="h5">Name on card</Typography>
      <Typography variant="body2">{name}</Typography>
    </ContactDetailsSc>
  );
};

export const getInitialFormData = (initialFormData) => {
  const floorNumber = _get(initialFormData, 'floor_number', '');
  const unitNumber = _get(initialFormData, 'unit_number', '');

  return {
    postalCode: _get(initialFormData, 'postal_code', ''),
    blockNumber: _get(initialFormData, 'block_number', ''),
    roadName: _get(initialFormData, 'road_name', ''),
    buildingName: _get(initialFormData, 'building_name', ''),
    floorNumber,
    unitNumber,
    // if user has no residential address
    // we treat it as there are floor and unit
    // so we uncheck the checkbox
    hasNoFloorUnit: !(_isEmpty(initialFormData) || (floorNumber && unitNumber)),
  };
};

const LpAddressFormContent = (props) => {
  const {
    handleOpenAddressForm,
    // Formik props
    values,
    errors,
    handleChange: handleFormikChange,
    handleBlur,
    isSubmitting,
    resetForm,
    submitForm,
    setFieldValue,
    isValid,
  } = props;

  // data states
  const [searchTerm, setSearchTerm] = React.useState('');
  const [suggestions, setSuggestions] = React.useState([]);

  // UI states
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [showResult, setShowResult] = React.useState(false);
  const [canSearch, setCanSearch] = React.useState(true);

  const enterManually = React.useCallback(() => {
    resetForm();
    setFieldValue('hasNoFloorUnit', false);
    setFieldValue('postalCode', searchTerm);
    setShowResult(false);
  }, [resetForm, searchTerm, setFieldValue]);

  React.useEffect(() => {
    if (!canSearch) {
      return;
    }

    const term = searchTerm.trim();

    if (term.length >= 4) {
      // trigger search
      setLoading(true);
      searchAddress(term, setSuggestions);
      setLoading(false);
      setShowResult(true);
    } else {
      setShowResult(false);
    }
  }, [searchTerm, canSearch]);

  const {
    postalCode,
    blockNumber,
    roadName,
    buildingName,
    hasNoFloorUnit,
    floorNumber,
    unitNumber,
  } = values;

  React.useEffect(() => {
    const isPostalCodeInvalid = typeof postalCode === 'undefined' || postalCode.length < 6;
    // enable/disable submit CTA
    const shouldDisableSubmitButton = !isValid || isPostalCodeInvalid;
    setIsSubmitButtonDisabled(shouldDisableSubmitButton);
  }, [isValid, values, isSubmitting, postalCode]);

  React.useEffect(() => {
    if (hasNoFloorUnit) {
      setFieldValue('floorNumber', '');
      setFieldValue('unitNumber', '');
    }
  }, [hasNoFloorUnit, setFieldValue]);

  React.useEffect(() => {
    setCanSearch(false);
    setSearchTerm(postalCode);
    setShowResult(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showPostalCodeError = Boolean(errors.postalCode);
  const showBlockNumberError = Boolean(errors.blockNumber);
  const showRoadNameError = Boolean(errors.roadName);
  const showFloorError = Boolean(errors.floorNumber);
  const showUnitError = Boolean(errors.unitNumber);

  const hasFloorUnitError = showFloorError || showUnitError;

  return (
    <Box mt={2}>
      <FormWrapper noBorder>
        <ProfileContent>
          <STYLED.SearchPostalCode>
            <Input
              data-testid="PostalCodeInput"
              type="text"
              name="postalCode"
              label="Postal code"
              onChange={(e) =>
                handleAddressInputChange(e, setCanSearch, setSearchTerm, handleFormikChange)
              }
              onBlur={handleBlur}
              required
              fullWidth
              value={postalCode}
              error={showPostalCodeError}
              inputIcon={
                <ButtonIcon type="button">
                  <SvgLocation />
                </ButtonIcon>
              }
            />
          </STYLED.SearchPostalCode>
          {showResult && (
            <STYLED.SearchResultDropdown>
              <STYLED.ResultListWrapper data-testid="AddressResultListWrapper">
                {renderSearchResult({
                  loading,
                  suggestions,
                  resetForm,
                  setFieldValue,
                  setCanSearch,
                  setShowResult,
                  searchTerm,
                  enterManually,
                })}
              </STYLED.ResultListWrapper>
            </STYLED.SearchResultDropdown>
          )}

          <FullAddressForm
            handleChange={(e) =>
              handleAddressInputChange(e, setCanSearch, setSearchTerm, handleFormikChange)
            }
            handleBlur={handleBlur}
            blockNumber={blockNumber}
            showBlockNumberError={showBlockNumberError}
            errors={errors}
            roadName={roadName}
            showRoadNameError={showRoadNameError}
            buildingName={buildingName}
            floorNumber={floorNumber}
            hasFloorUnitError={hasFloorUnitError}
            unitNumber={unitNumber}
            hasNoFloorUnit={hasNoFloorUnit}
          />

          <FormAction>
            <ButtonContained
              color="grey"
              data-testid="EditAddressCancel"
              onClick={() => handleOpenAddressForm(false)}
            >
              Cancel
            </ButtonContained>

            <ButtonContained
              data-testid="SubmitAddressCta"
              onClick={submitForm}
              disabled={isSubmitButtonDisabled}
              loading={isSubmitting}
            >
              Submit
            </ButtonContained>
          </FormAction>
        </ProfileContent>
        <ProfileAction className="ndsHidden-s-down"></ProfileAction>
      </FormWrapper>
    </Box>
  );
};

export default LpAddressFormContent;
